<template>
  <Loader
    :value="promises.fetchOne"
    :message="$t('loading', { resource: $tc('residential', 2).toLowerCase() }) + '...'"
    class="residential"
  >
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb">
        <template #actions>
          <Actions placement="project-residential" :query="{ filter, sort }" />
          <DropdownComponent>
            <ButtonComponent :icon="['fal', 'plus']" :label="$tc('create')" theme="primary" />
            <template #dropdown>
              <ButtonComponent
                :label="$t('projectShopsCreateResidentials')"
                theme="secondary"
                :icon="['fal', 'plus']"
                tag="router-link"
                class="text--small"
                :to="{
                  name: 'project-edit',
                  params: { id: project.id, step: 'residentials' },
                  query: { create: true },
                }"
              />
            </template>
          </DropdownComponent>
        </template>
      </HeaderComponent>
      <div class="residentials__filter row between mb-2">
        <SortComponent v-model="sort" :items="sortableBy" />
        <FilterComponent v-model="filter" :items="[]" style="display: none" />
      </div>

      <DataTable
        v-if="!isEmpty"
        :reset-selected-on-update="true"
        :paginate="true"
        sticky="var(--header-height)"
        @update:selected="val => (selected = val)"
        :sort-by.sync="sort"
        :sortable-by="sortableBy.map(s => s.key)"
        :value="items"
        :row-wrapper="
          item => ({
            component: $store.getters['Project/hasRole']('manager') ? 'router-link' : 'div',
            props: {
              to: { name: 'residential-edit', params: { id: item.id } },
              style: { color: 'inherit', textDecoration: 'inherit' },
            },
          })
        "
        :columns="columns"
      >
        <template #number="{ item }">{{ item.number }}</template>
        <template #groups="{ item }">{{ item.groups ? item.groups.join(', ') : null }}</template>
        <template #numberOfBedrooms="{ item }">{{ getPropertyType(item.properties, 'numberOfBedrooms') }}</template>
        <template #useableArea="{ item }">{{ getPropertyType(item.properties, 'useableArea') }}</template>
        <template #owners="{ item }">
          <Avatar :value="getOwners(item)" size="small" />
        </template>
        <template v-slot:propertyType="{ item }">{{ getPropertyType(item.properties, 'propertyType') }}</template>
        <!-- <template #slot="{ item }">
          <SlotComponent placement="residential:list" :query="{ residential: item.id }" />
        </template> -->
        <template #price="{ item }">
          {{ getPrice(item) }}
        </template>
        <template #status="{ item }">
          <Badge
            :value="$t(`residentialStatus:${item.sale.type}.${item.status}`)"
            :theme="getStatusTheme(item.status)"
            type="outlined"
          />
        </template>
        <template #primary-row-actions="{ item }">
          <ButtonComponent
            v-if="$store.getters['Project/hasRole'](['manager', 'complaint.manager'])"
            tag="router-link"
            v-tooltip="$t('residentialAccount')"
            :icon="['fal', 'home']"
            :to="{ name: 'residential-edit', params: { id: item.id, step: 'residential-account' } }"
          />
          <ButtonComponent
            v-if="addons.includes('project:complaint') && $store.getters['Project/hasRole']('complaint.manager')"
            tag="router-link"
            v-tooltip="$tc('complaint', 2)"
            :icon="['fal', 'tasks']"
            :to="{
              name: 'residential-complaints',
              params: { id: item.id },
            }"
          />
        </template>
        <template #secondary-row-actions="{ item, visible }">
          <Actions v-if="visible && project" placement="residential" :query="{ residential: item.id }" type="list" />
          <hr />
          <ButtonComponent
            v-if="$store.getters['Project/hasRole']('manager')"
            tag="router-link"
            :label="$t('edit')"
            :icon="['fal', 'pen']"
            :to="{ name: 'residential-edit', params: { id: item.id } }"
          />
          <ButtonComponent
            v-if="$store.getters['Project/hasRole'](/^shop/)"
            tag="router-link"
            :label="$tc('addonShop', 1)"
            :icon="['fal', 'ruler-triangle']"
            :to="{ name: 'residential-edit', params: { id: item.id, step: 'shop' } }"
          />
          <ButtonComponent
            v-if="$store.getters['Project/hasRole'](/signingAuthority$/)"
            tag="router-link"
            :label="$tc('document', 2)"
            :icon="['fal', 'file-contract']"
            :to="{
              name: 'residential-contracts',
              params: { id: item.id },
            }"
          />
          <ButtonComponent
            v-if="addons.includes('project:presentation')"
            tag="a"
            :href="item.url"
            target="_blank"
            :label="$t('preview')"
            :icon="['fal', 'eye']"
          />
          <ButtonComponent
            v-if="$store.getters['Project/hasRole'](['manager', 'complaint.manager'])"
            tag="router-link"
            :label="$t('residentialAccount')"
            :icon="['fal', 'home']"
            :to="{ name: 'residential-edit', params: { id: item.id, step: 'residential-account' } }"
          />
          <ButtonComponent
            v-if="addons.includes('project:complaint') && $store.getters['Project/hasRole']('complaint.manager')"
            tag="router-link"
            :label="$tc('complaint', 2)"
            :icon="['fal', 'tasks']"
            :to="{
              name: 'residential-complaints',
              params: { id: item.id },
            }"
          />
          <ButtonComponent
            v-if="$store.getters.isSuperadmin"
            :label="$t('delete')"
            @click="context = item"
            :icon="['fal', 'times']"
          />
        </template>
        <template #select-actions>
          <ButtonComponent :label="$t('edit')" :icon="['fal', 'pen']" @click="update = true" />
        </template>
      </DataTable>
      <EmptyState v-else :title="$t('noX', { x: $tc('unit', 2).toLowerCase() })" :icon="['fal', 'home']">
        <template #actions>
          <ButtonComponent
            :label="$t('projectShopsCreateResidentials')"
            theme="secondary"
            :icon="['fal', 'plus']"
            tag="router-link"
            class="text--small"
            :to="{
              name: 'project-edit',
              params: { id: project.id, step: 'residentials' },
              query: { create: true },
            }"
          />
        </template>
      </EmptyState>
    </div>
    <RemoveComponent
      :show.sync="removeDialog"
      :title="$path('number', context || {})"
      :action="() => remove({ id: context.id })"
      @onSuccess="onRemove"
      :resource="$tc('residential')"
    />
    <UpdateComponent
      v-if="update"
      @close="update = false"
      @done="onUpdateDone"
      :user-selection="selected"
      :disabledFields="$path('metadata.disabledFields.unit', project)"
      :project="project"
    />
  </Loader>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { Debounce } from '@/utils'
import { Currency } from '@/filters'
import PropertiesFilterTransform from '@/components/Residential/Properties/filterTransform'

import SortComponent, { SortWatcher, getSort, SortLocal } from '@/components/Sort'
import Actions from '@/components/Actions'
import { DropdownComponent } from 'vue-elder-dropdown'
import RemoveComponent from '@/components/Residential/Remove'
import UpdateComponent from '@/components/Residential/Update'
import HeaderComponent from '@/components/Header'
import FilterMixin from '@/components/Filter'
import EmptyState from '@/components/EmptyState'
import DataTable from '@/components/DataTable'
import Badge from '@/components/Badge'
import Avatar from '@/components/Avatar'
import { translationMap } from '../../../utils/analytics'
import tracker from '@/tracking'
import Bus from '@/bus'

export default {
  mixins: [
    FilterMixin({
      default: {
        search: '',
        number: undefined,
        propertyType: undefined,
        numberOfBedrooms: undefined,
        status: undefined,
      },
    }),
  ],
  props: {
    id: String,
  },
  watch: {
    id: {
      handler: 'fetch',
      immediate: true,
    },
    filter: {
      handler() {
        this.fetch('filter')
      },
      deep: true,
      immediate: true,
    },
    ...SortWatcher('fetchSortWrapper'),
  },
  data() {
    return {
      update: false,
      context: null,
      sort: { number: 1 },
      sortableBy: [
        { key: 'number', label: this.$t('number') },
        // { key: 'slot' },
        { key: 'propertyType', label: this.$t('propertyType'), virtual: true, type: this.handlePropertySort },
        { key: 'numberOfBedrooms', label: this.$t('numberOfBedrooms'), virtual: true, type: this.handlePropertySort },
        { key: 'useableArea', label: this.$t('useableArea'), virtual: true, type: this.handlePropertySort },
        // { key: 'groups', label: this.$tc('group', 2) },
        { key: 'status', label: this.$t('status') },
        { key: 'price', label: this.$t('price') },
      ],
      selected: {
        items: [],
      },
    }
  },
  computed: {
    ...mapState('Project', ['item', 'promises', 'states']),
    columns() {
      return [
        { id: 'number', label: this.$t('number') },
        // { id: 'slot' },
        { id: 'propertyType', label: this.$t('propertyType') },
        // { id: 'groups', label: $tc('group', 2) },
        { id: 'numberOfBedrooms', label: this.$t('numberOfBedrooms'), align: 'end' },
        this.item && this.item.residentials && this.item.residentials.some(residential => residential.owners.length > 0)
          ? { id: 'owners', label: this.$tc('owner', 2) }
          : null,
        { id: 'useableArea', label: this.$t('useableArea'), align: 'end' },
        { id: 'status', label: this.$t('status') },
        { id: 'price', label: this.$t('price'), align: 'end' },
      ].filter(Boolean)
    },
    breadcrumb() {
      return [
        { title: this.$tc('project', 2), action: { name: 'projects' } },
        {
          title: this.item.name,
          action: { name: 'project-dashboard', params: { id: this.id } },
        },
        { title: this.$metaInfo.title },
      ]
    },
    addons() {
      return this.$path('metadata.addons', this.item) || []
    },
    project() {
      return this.item || {}
    },
    isEmpty() {
      return !this.states.fetchOne && !this.$path('items.length')
    },
    hasFilters() {
      return Object.values(this.filter).some(Boolean)
    },
    items: SortLocal('item.residentials'),
    removeDialog: {
      get() {
        return Boolean(this.context)
      },
      set(val) {
        if (!val) this.context = null
      },
    },
    create: {
      get() {
        return ['true', true].includes(this.$route.query.create)
      },
      set(val) {
        this.$router.replace({ query: { ...this.$route.query, create: val ? true : undefined } })
      },
    },
    residentialNumbers() {
      if (!this.item || !this.item.allResidentials || this.item.allResidentials.length === 0) return []

      return this.item.allResidentials.map(({ number, displayName }) => ({
        id: `${number}`,
        value: `${number}`,
        label: `${displayName || number}`,
        name: `${displayName || number}`,
      }))
    },
    filterOptions() {
      return {
        number: {
          transformFilter: { get: v => (v.number || '').split(','), set: v => ({ number: v.join(',') }) },
          icon: ['fal', 'tags'],
          label: this.$t('number'),
          items: this.residentialNumbers,
        },
        search: {
          placeholder: [this.$tc('residential', 1)],
        },
        propertyType: {
          transformFilter: PropertiesFilterTransform('propertyType'),
          icon: ['fal', 'tags'],
          items: this.getPropertyItems('propertyType'),
          label: this.$tc('propertyType', 2),
        },
        numberOfBedrooms: {
          transformFilter: PropertiesFilterTransform('numberOfBedrooms'),
          icon: ['fal', 'ruler-triangle'],
          items: this.getPropertyItems('numberOfBedrooms'),
          label: this.$t('numberOfBedrooms'),
        },
        status: { icon: ['fal', 'tags'], items: this.getPropertyItems('status'), label: this.$t('status') },
      }
    },
  },
  methods: {
    ...mapActions('Project', {
      fetch: Debounce(function (dispatch, v) {
        if (v) {
          if (Object.values(this[v]).filter(Boolean).length) tracker.emit(`ProjectResidentalList:${v}`, { ...this[v] })
        }

        return dispatch('fetch', [
          {
            id: this.id,
            filter: this.filter,
            leadReferences: [],
            sort: Object.fromEntries(
              Object.entries(getSort.call(this)).flatMap(([key, value]) => [
                ['key', key],
                ['value', value],
              ]),
            ),
          },
          { query: 'ProjectResidentalList' },
        ])
      }, 200),
    }),
    ...mapActions('Residential', ['remove']),
    fetchSortWrapper() {
      this.fetch('sort')
    },
    onRemove() {
      this.reset()
      Bus.$emit('data-table:reset')
    },
    getTranslation(keys = [], separator = ' ') {
      keys = Array.isArray(keys) ? keys : [keys]
      if (keys.length === 1) return this.$tc(translationMap[keys[0]] || keys[0], 1)
      return keys.map(key => this.$tc(translationMap[key] || key, 1)).join(separator)
    },
    getPropertyItems(key) {
      if (
        !this.item ||
        !this.item.metadata ||
        !this.item.metadata.residentialProperties ||
        !this.item.metadata.residentialProperties[key]
      )
        return []
      return this.item.metadata.residentialProperties[key]
        .map(property => {
          if (!property && property !== 0) return

          return {
            id: `${property}`,
            value: `${property}`,
            label: this.getTranslation(property) || `${property}`,
            name: this.getTranslation(property) || `${property}`,
          }
        })
        .filter(Boolean)
    },
    handlePropertySort({ key, items }) {
      const values = items.reduce(
        (agg, item) => {
          const property = item.properties && item.properties.find(property => property.key === key)
          if (!property || [null, undefined, 'null', 'undefined', ''].includes(property.value)) {
            agg.others.push(item)
          } else {
            agg.sortable.push(item)
          }
          return agg
        },
        { sortable: [], others: [] },
      )
      return [
        ...values.sortable.sort((a, b) => {
          const propertyA = a.properties && a.properties.find(property => property.key === key)
          const propertyB = b.properties && b.properties.find(property => property.key === key)

          if (propertyA.value < propertyB.value) {
            return -1 * this.sort[key]
          }
          if (propertyA.value > propertyB.value) {
            return 1 * this.sort[key]
          }
          return 0
        }),
        ...values.others,
      ]
    },
    onUpdateDone() {
      this.update = false
      this.reset()
    },
    getPrice(item) {
      let price = Currency(this.$path('sale.value', item))

      switch (item.sale.type) {
        case 'rent':
          if ([null, undefined].includes(price)) return this.$t('priceOnRequest')
          return `${price} / ${this.$t('multiplicatorMonthly')}.`
        default:
          if ([null, undefined].includes(price)) return this.$t('priceOnRequest')
          return price
      }
    },
    getPropertyType(properties, type) {
      const property = properties && properties.find(property => property.key === type)

      if (property) return this.getTranslation(property.value) || property.value

      return null
    },
    getOwners(item) {
      if (!this.$path('owners', item)) return []
      return item.owners.map(owner => ({ name: owner.name, src: owner.avatar }))
    },
    getStatusTheme(status) {
      switch (status) {
        case 'sale':
        case 'rent':
          return 'success'
        case 'sold':
        case 'rented':
          return 'error'
        case 'reserved':
          return 'warning'
        default:
          return 'default'
      }
    },
    reset() {
      // this.selected = []
      this.fetch()
    },
  },
  metaInfo() {
    return {
      title: this.$tc('residential', 2),
    }
  },
  components: {
    Avatar,
    HeaderComponent,
    SortComponent,
    RemoveComponent,
    UpdateComponent,
    Actions,
    DropdownComponent,
    EmptyState,
    DataTable,
    Badge,
  },
  filters: {
    Currency,
  },
}
</script>

<style lang="scss">
.residential {
  background-color: $background-grey;
}
</style>
